<script lang="ts" setup>
import { useRouter, useRoute } from 'vue-router'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n';
import messages from '@/locales/account/messages'
import { loginUser } from '@/api/user';
import { useVuelidate } from '@vuelidate/core'
import { required, email, minLength, maxLength, helpers } from '@vuelidate/validators'
import { useCountriesStore } from '@/stores/countries';
import 'flag-icons/css/flag-icons.min.css';
import 'v-phone-input/dist/v-phone-input.css';
import { VPhoneInput } from 'v-phone-input';
import MakePhone from '@/utils/MakePhone';
import { createUser } from '@/api/user';
import { useToast, POSITION } from 'vue-toastification'



const countriesStore = useCountriesStore();
const { t, locale } = useI18n({
    useScope: 'global',
    messages
})
const toast = useToast()
const router = useRouter()
const route = useRoute()
const email = ref('')
const password = ref('')
const account_type = ref('individual')
const invoice_address = ref('')
const delivery_address = ref('')
const invoice_not_find = ref(false)
const invoice_not_find2 = ref(false)
const sameDelivery = ref(true)
const signupForm = ref({
    lang:'',
    email: '',
    password: '',
    password_confirmation: '',
    title: '',
    lastname: '',
    firstname: '',
    company: '',
    vat_number: '',
    invoice_address: {
        address1: '',
        address2: '',
        zip: '',
        city: '',
        country: ''
    },
    differentDelivery: false,
    phone_number: '',
    delivery_address: {
        title:'',
        lastname: '',
        firstname: '',
        company: '',
        address1: '',
        address2: '',
        zip: '',
        city: '',
        country: ''
    },
    newsletter: undefined,
    cgv: false
})
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Use a new RegExp instance and remove the name parameter
const validEmail = helpers.regex(new RegExp(emailRegex.source, emailRegex.flags));

const rules = computed(() => {
    let localRules= {
        email: { required, validEmail },
        password: { 
            required, 
            minLength: minLength(6), 
            hasSpecialChar: (value) => /[^\w\s]/.test(value), // Matches any non-word, non-space character
            //hasSpecialChar: (value) => /[!@#$%^&*(),.?":{}|<>]/.test(value),
            hasNumber: (value) => /[0-9]/.test(value),
            hasLetter: (value) => /[a-zA-Z]/.test(value)},
        password_confirmation: { required, minLength: minLength(6), sameAs: (newVal) => newVal === signupForm.value.password },
        title: { required },
        lastname: { required },
        firstname: { required },
        company: { },
        vat_number: { },
        invoice_address: {
            address1: { required, minLength: minLength(5), maxLength: maxLength(50) },
            address2: { maxLength: maxLength(50) },
            zip: { required , maxlength: maxLength(10) },
            city: { required, maxLength: maxLength(50) },
            country: { required }
        },
        phone_number: { required, valid: (value) => MakePhone(value).valid },
        newsletter: { required },
        cgv: { required, accepted: (value) => value }
    }
    if(signupForm.value.differentDelivery){
        localRules.delivery_address = {
            title: { required },
            lastname: { required },
            firstname: { required },
            company: { },
            address1: { required, minLength: minLength(5), maxLength: maxLength(50) },
            address2: { maxLength: maxLength(50) },
            zip: { required , maxlength: maxLength(10) },
            city: { required, maxLength: maxLength(50) },
            country: { required }
        }
    }
    return localRules
    
})
const v$ = useVuelidate(rules, signupForm)
const login = async () => {
    loginUser(email.value, password.value).then(()=>{
        if(route.query.redirect) {
            router.push(route.query.redirect as string)
        }
        else{
            router.push({name: locale.value+'.account'})
        }
    }).catch((err)=>{
        console.log(err);
        toast.error(err.response.data.content[locale.value],{
            position: POSITION.TOP_CENTER
        })
    })
}
const singup = () => {
    v$.value.$touch()
    if(v$.value.$error){
        toast.error(t('account.signup.error'),{
            position: POSITION.TOP_CENTER
        })
        return
    }
    const payload = {... signupForm.value}
    if(!payload.differentDelivery){
        payload.delivery_address = {
            title: payload.title,
            lastname: payload.lastname,
            firstname: payload.firstname,
            company: payload.company,
            address1: payload.invoice_address.address1,
            address2: payload.invoice_address.address2,
            zip: payload.invoice_address.zip,
            city: payload.invoice_address.city,
            country: payload.invoice_address.country
        }
    }
    payload.lang = locale.value
    createUser(payload).then((res)=>{
        toast.success(t('account.signup.success'),{
            position: POSITION.TOP_RIGHT
        })
        trackSignUp()
        if(route.query.redirect) {
            router.push(route.query.redirect as string)
        }
        else{
            router.push({name: locale.value+'.account'})
        }
    }).catch((err)=>{
        toast.error(err.response.data.content[locale.value],{
            position: POSITION.TOP_CENTER
        })
    })
    //router.push({name: 'account.singup'})
}
function trackSignUp() {
  if (window.dataLayer) {
    window.dataLayer.push({
      event: 'signUp',
      eventCategory: 'User',
      eventAction: 'Create Account',
      eventLabel: 'User Registration'
    });
  }

  if (window.fbq) {
    window.fbq('track', 'CompleteRegistration');
  }
}

function selectInvoiceAddress(addressResult){
    // the fields address1 and address2 are max 50 characters, we need to split the address in two part if it's too long
    let address = addressResult.address.houseNumber + ' ' + addressResult.address.street
    if(address.length>50){
        // we split the end of the address
        let split = address.split(' ')
        let address1 = split.slice(0, Math.floor(split.length/2)).join(' ')
        let address2 = split.slice(Math.floor(split.length/2)).join(' ')
        v$.value.invoice_address.address1.$model = address1
        v$.value.invoice_address.address2.$model = address2
    }
    else{
        v$.value.invoice_address.address1.$model = address
        v$.value.invoice_address.address2.$model = ''
    }
    v$.value.invoice_address.address1.$touch()
    v$.value.invoice_address.address2.$touch()

    v$.value.invoice_address.zip.$model = addressResult.address.postalCode
    v$.value.invoice_address.zip.$touch()
    v$.value.invoice_address.city.$model = addressResult.address.city
    v$.value.invoice_address.city.$touch()
    v$.value.invoice_address.country.$model = countriesStore.getCountryIso3(addressResult.address.countryCode).iso
    v$.value.invoice_address.country.$touch()
}
function selectDeliveryAddress(addressResult){
    // the fields address1 and address2 are max 50 characters, we need to split the address in two part if it's too long
    let address = addressResult.address.houseNumber + ' ' + addressResult.address.street
    if(address.length>50){
        // we split the end of the address
        let split = address.split(' ')
        let address1 = split.slice(0, Math.floor(split.length/2)).join(' ')
        let address2 = split.slice(Math.floor(split.length/2)).join(' ')
        v$.value.delivery_address.address1.$model = address1
        v$.value.delivery_address.address2.$model = address2
    }
    else{
        v$.value.delivery_address.address1.$model = address
        v$.value.delivery_address.address2.$model = ''
    }
    v$.value.delivery_address.address1.$touch()
    v$.value.delivery_address.address2.$touch()

    v$.value.delivery_address.zip.$model = addressResult.address.postalCode
    v$.value.delivery_address.zip.$touch()
    v$.value.delivery_address.city.$model = addressResult.address.city
    v$.value.delivery_address.city.$touch()
    v$.value.delivery_address.country.$model = countriesStore.getCountryIso3(addressResult.address.countryCode).iso
    v$.value.delivery_address.country.$touch()
}
</script>
<template>
    <div class="head-login">
        <form @submit.prevent="login">
            <h1>{{$t('account.login.connexion')}}</h1>
            <h2>{{$t('account.login.allready_client')}}</h2>
            <label for="email">{{$t('account.login.email')}}</label>
            <input type="text" id="email" v-model="email" >
            <label for="password">{{$t('account.login.password')}}</label>
            <input type="password" id="password" v-model="password">
            <button type="submit">{{$t('account.login.connect')}}</button>
            <router-link :to="{name:locale+'.account.lost'}">{{$t('account.login.forgot_password')}}</router-link>
        </form>
    </div>
    <div class="content">

        <h2>{{$t('account.signup.new_client')}}</h2>
        <form @submit.prevent="singup" class="white-bg">
            <div>
                <fieldset>
                    <legend>{{$t('account.signup.email_password')}}</legend>
                    <label for="email_signup">{{$t('account.signup.email')}}</label>
                    <input type="text" id="email_signup" v-model="v$.email.$model" @blur="v$.email.$touch" :class="[{error:(v$.email.$dirty && v$.email.$error)},{valid:(v$.email.$dirty && !v$.email.$error)}]" >
                    <div v-if="v$.email.$dirty && v$.email.$error" class="error">{{$t('account.signup.email_error')}}</div>
                    <label for="password_signup">{{$t('account.signup.password')}}</label>
                    <input type="password" id="password_signup" v-model="v$.password.$model" @blur="v$.password.$touch"  :class="[{error:(v$.password.$dirty && v$.password.$error)},{valid:(v$.password.$dirty && !v$.password.$error)}]" >
                    <div v-if="v$.password.$dirty && v$.password.$error" class="error">{{$t('account.signup.password_error')}}</div>
                    <label for="password_confirmation">{{$t('account.signup.confirm_password')}}</label>
                    <input type="password" id="password_confirmation" v-model="v$.password_confirmation.$model" @blur="v$.password_confirmation.$touch"  :class="[{error:(v$.password_confirmation.$dirty && v$.password_confirmation.$error)},{valid:(v$.password_confirmation.$dirty && !v$.password_confirmation.$error)}]" >
                    <div v-if="v$.password_confirmation.$dirty && v$.password_confirmation.$error" class="error">{{$t('account.signup.confirm_password_error')}}</div>
                </fieldset>
                <fieldset>
                    <legend>{{$t('account.signup.personnal_information')}}</legend>
                    <label>{{$t('account.signup.buying_as')}} :</label>
                    <label><input type="radio" value="individual" v-model="account_type"> {{$t('account.signup.individual')}}</label>
                    <label><input type="radio" value="company" v-model="account_type"> {{$t('account.signup.company')}}</label>
                    <p>
                        {{$t('account.signup.buying_as_explain')}}
                    </p>
                    <label for="title">{{$t('account.signup.title')}}</label>
                    <select id="title" v-model="v$.title.$model" :class="[{error:(v$.title.$dirty && v$.title.$error)},{valid:(v$.title.$dirty && !v$.title.$error)}]">
                        <option value="Mr">{{$t('account.signup.Mr')}}</option>
                        <option value="Mme">{{$t('account.signup.Mrs')}}</option>
                        <option value="Mlle">{{$t('account.signup.Miss')}}</option>
                    </select>
                    <div v-if="v$.title.$dirty && v$.title.$error" class="error">{{$t('account.signup.title_error')}}</div>
                    <label for="lastname">{{$t('account.signup.last_name')}}</label>
                    <input type="text" maxlength="50" id="lastname" v-model="v$.lastname.$model" :class="[{error:(v$.lastname.$dirty && v$.lastname.$error)},{valid:(v$.lastname.$dirty && !v$.lastname.$error)}]">
                    <div v-if="v$.lastname.$dirty && v$.lastname.$error" class="error">{{$t('account.signup.last_name_error')}}</div>
                    <label for="firstname">{{$t('account.signup.first_name')}}</label>
                    <input type="text" maxlength="50" id="firstname" v-model="v$.firstname.$model" :class="[{error:(v$.firstname.$dirty && v$.firstname.$error)},{valid:(v$.firstname.$dirty && !v$.firstname.$error)}]">
                    <div v-if="v$.firstname.$dirty && v$.firstname.$error" class="error">{{$t('account.signup.first_name_error')}}</div>
                    <div v-if="account_type=='company'">
                        <label for="company">{{$t('account.signup.company_name')}}</label>
                        <input type="text" maxlength="50" id="company" v-model="v$.company.$model">
                        <label for="vat_number">{{$t('account.signup.vat_number')}}</label>
                        <input type="text" maxlength="20" id="vat_number" v-model="v$.vat_number.$model">
                    </div>
                </fieldset>
            </div>
            <div>
                <fieldset>
                    <legend>{{ signupForm.differentDelivery? $t('account.signup.invoice_and_delivery_address'):$t('account.signup.invoice_address')}}</legend>
                    <label>{{$t('account.signup.search_address')}}</label>
                    <AddressFinder v-model="invoice_address" :readonly="invoice_not_find" @selected="selectInvoiceAddress" :placeholder="$t('account.signup.search_address_placeholder')" />
                    <label><input type="checkbox" v-model="invoice_not_find"> {{$t('account.signup.not_find_address')}}</label>
                    <label for="address_inv1">{{$t('account.signup.address')}} <i>{{$t('account.signup.street_number')}}</i></label>
                    <input type="text" maxlength="50" id="address_inv1" :readonly="!invoice_not_find" v-model="v$.invoice_address.address1.$model" :class="[{error:(v$.invoice_address.address1.$dirty && v$.invoice_address.address1.$error)},{valid:(v$.invoice_address.address1.$dirty && !v$.invoice_address.address1.$error)}]">
                    <div v-if="v$.invoice_address.address1.$dirty && v$.invoice_address.address1.$error" class="error">{{$t('account.signup.address_error')}}</div>
                    <input type="text" maxlength="50" id="address_inv2" :readonly="!invoice_not_find" v-model="v$.invoice_address.address2.$model" class="mt-2" :class="[{error:(v$.invoice_address.address2.$dirty && v$.invoice_address.address2.$error)},{valid:(v$.invoice_address.address2.$dirty && !v$.invoice_address.address2.$error)}]">
                    <div class="d-flex ga-4">
                        <div class="w-33">
                            <label for="zip_inv">{{$t('account.signup.zip')}}</label>
                            <input type="text" maxlength="10" id="zip_inv" :readonly="!invoice_not_find" v-model="v$.invoice_address.zip.$model" :class="[{error:(v$.invoice_address.zip.$dirty && v$.invoice_address.zip.$error)},{valid:(v$.invoice_address.zip.$dirty && !v$.invoice_address.zip.$error)}]">
                            <div v-if="v$.invoice_address.zip.$dirty && v$.invoice_address.zip.$error" class="error">{{$t('account.signup.zip_error')}}</div>
                        </div>
                        <div class="w-66">
                            <label for="city_inv">{{$t('account.signup.city')}}</label>
                            <input type="text" maxlength="50" id="city_inv" :readonly="!invoice_not_find" v-model="v$.invoice_address.city.$model" :class="[{error:(v$.invoice_address.city.$dirty && v$.invoice_address.city.$error)},{valid:(v$.invoice_address.city.$dirty && !v$.invoice_address.city.$error)}]">
                            <div v-if="v$.invoice_address.city.$dirty && v$.invoice_address.city.$error" class="error">{{$t('account.signup.city_error')}}</div>
                        </div>
                    </div>
                    <label for="country_inv">{{$t('account.signup.country')}}</label>
                    <select id="country_inv" :readonly="!invoice_not_find" v-model="v$.invoice_address.country.$model" :class="[{error:(v$.invoice_address.country.$dirty && v$.invoice_address.country.$error)},{valid:(v$.invoice_address.country.$dirty && !v$.invoice_address.country.$error)}]">
                        <option v-for="country in countriesStore.countries" :key="country.iso" :value="country.iso">{{ country.content[locale] }}</option>
                    </select>
                    <div v-if="v$.invoice_address.country.$dirty && v$.invoice_address.country.$error" class="error">{{$t('account.signup.country_error')}}</div>
                    <label for="phone_number">{{$t('account.signup.cell_phone')}}</label>
                    <v-phone-input
      v-model="v$.phone_number.$model"
      :preferCountries="countriesStore.countries.map(c => c.iso)"
      :includeCountries="countriesStore.countries.map(c => c.iso)"
      displayFormat="international"
      guessCountry
      label=""
      countryLabel=""
      variant="plain"
      hide-details
      density="compact"
      :single-line="true"
      country-icon-mode="svg"
      :invalid-message="null"
      class="input"
    :class="[{error:(v$.phone_number.$dirty && v$.phone_number.$error)},{valid:(v$.phone_number.$dirty && !v$.phone_number.$error)}]"
  />
                    <div v-if="v$.phone_number.$dirty && v$.phone_number.$error" class="error">{{$t('account.signup.cell_phone_error')}}</div>
                    <p>{{$t('account.signup.phone_explain')}}</p>
                </fieldset>
                <label><input type="checkbox" v-model="signupForm.differentDelivery" /> {{$t('account.signup.different_delivery_address')}}</label>
                <fieldset v-if="signupForm.differentDelivery">
                    <legend>{{$t('account.signup.delivery_address')}}</legend>
                    <label for="title_delivery">{{$t('account.signup.title')}}</label>
                    <select id="title_delivery" v-model="v$.delivery_address.title.$model" :class="[
                        {error:(v$.delivery_address.title.$dirty && v$.delivery_address.title.$error)},
                        {valid:(v$.delivery_address.title.$dirty && !v$.delivery_address.title.$error)}]">
                        <option value="Mr">{{$t('account.signup.Mr')}}</option>
                        <option value="Mme">{{$t('account.signup.Mrs')}}</option>
                        <option value="Mlle">{{$t('account.signup.Miss')}}</option>
                    </select>
                    <div v-if="v$.delivery_address.title.$dirty && v$.delivery_address.title.$error" class="error">{{$t('account.signup.title_error')}}</div>
                    <label for="lastname_delivery">{{$t('account.signup.last_name')}}</label>
                    <input type="text" maxlength="50" id="lastname_delivery" v-model="v$.delivery_address.lastname.$model" :class="[
                        {error:(v$.delivery_address.lastname.$dirty && v$.delivery_address.lastname.$error)},
                        {valid:(v$.delivery_address.lastname.$dirty && !v$.delivery_address.lastname.$error)}]">
                    <div v-if="v$.delivery_address.lastname.$dirty && v$.delivery_address.lastname.$error" class="error">{{$t('account.signup.last_name_error')}}</div>
                    <label for="firstname_delivery">{{$t('account.signup.first_name')}}</label>
                    <input type="text" maxlength="50" id="firstname_delivery" v-model="v$.delivery_address.firstname.$model" :class="[
                        {error:(v$.delivery_address.firstname.$dirty && v$.delivery_address.firstname.$error)},
                        {valid:(v$.delivery_address.firstname.$dirty && !v$.delivery_address.firstname.$error)}]">
                    <div v-if="v$.delivery_address.firstname.$dirty && v$.delivery_address.firstname.$error" class="error">{{$t('account.signup.first_name_error')}}</div>
                    <label for="company_delivery">{{$t('account.signup.company_name')}}</label>
                    <input type="text" maxlength="50" id="company_delivery" v-model="v$.delivery_address.company.$model">
                    <label>{{$t('account.signup.search_address')}}</label>
                    <AddressFinder v-model="delivery_address" :readonly="invoice_not_find2" @selected="selectDeliveryAddress" :placeholder="$t('account.signup.search_address_placeholder')" />
                    <label><input type="checkbox" v-model="invoice_not_find2"> {{$t('account.signup.not_find_address')}}</label>
                    <label for="address_delivery1">{{$t('account.signup.address')}} <i>{{$t('account.signup.street_number')}}</i></label>
                    <input type="text" maxlength="50" id="address_delivery1" :readonly="!invoice_not_find2" v-model="v$.delivery_address.address1.$model" :class="[
                        {error:(v$.delivery_address.address1.$dirty && v$.delivery_address.address1.$error)},
                        {valid:(v$.delivery_address.address1.$dirty && !v$.delivery_address.address1.$error)}]">
                    <div v-if="v$.delivery_address.address1.$dirty && v$.delivery_address.address1.$error" class="error">{{$t('account.signup.address_error')}}</div>
                    <input type="text" maxlength="50" id="address_delivery2" :readonly="!invoice_not_find2" v-model="v$.delivery_address.address2.$model" class="mt-2" :class="[
                        {error:(v$.delivery_address.address2.$dirty && v$.delivery_address.address2.$error)},
                        {valid:(v$.delivery_address.address2.$dirty && !v$.delivery_address.address2.$error)}]">
                    <div class="d-flex ga-4">
                        <div class="w-33">
                            <label for="zip_delivery">{{$t('account.signup.zip')}}</label>
                            <input type="text" maxlength="10" id="zip_delivery" :readonly="!invoice_not_find2" v-model="v$.delivery_address.zip.$model" :class="[
                                {error:(v$.delivery_address.zip.$dirty && v$.delivery_address.zip.$error)},
                                {valid:(v$.delivery_address.zip.$dirty && !v$.delivery_address.zip.$error)}]">
                            <div v-if="v$.delivery_address.zip.$dirty &&
                                v$.delivery_address.zip.$error" class="error">{{$t('account.signup.zip_error')}}</div>
                        </div>
                        <div class="w-66">
                            <label for="city_delivery">{{$t('account.signup.city')}}</label>
                            <input type="text" maxlength="50" id="city_delivery" :readonly="!invoice_not_find2" v-model="v$.delivery_address.city.$model" :class="[
                                {error:(v$.delivery_address.city.$dirty && v$.delivery_address.city.$error)},
                                {valid:(v$.delivery_address.city.$dirty && !v$.delivery_address.city.$error)}]">
                            <div v-if="v$.delivery_address.city.$dirty && 
                            v$.delivery_address.city.$error" class="error">{{$t('account.signup.city_error')}}</div>
                        </div>
                    </div>
                    <label for="country_delivery">{{$t('account.signup.country')}}</label>
                    <select id="country_delivery" :readonly="!invoice_not_find2" v-model="v$.delivery_address.country.$model" :class="[
                        {error:(v$.delivery_address.country.$dirty && v$.delivery_address.country.$error)},
                        {valid:(v$.delivery_address.country.$dirty && !v$.delivery_address.country.$error)}]">
                        <option v-for="country in countriesStore.countries" :key="country.iso" :value="country.iso">{{ country.content[locale] }}</option>
                    </select>
                    <div v-if="v$.delivery_address.country.$dirty && v$.delivery_address.country.$error" class="error">{{$t('account.signup.country_error')}}</div>
                </fieldset>
                <fieldset>
                    <legend>{{$t('account.signup.newsletter')}}</legend>
                    <p>{{$t('account.signup.newsletter_explain')}}</p>
                    <label :class="[
                        {error:(v$.newsletter.$dirty && v$.newsletter.$error)}]"><input type="radio" :value="true" v-model="v$.newsletter.$model" /> {{$t('account.signup.yes')}}</label>
                    <label :class="[
                        {error:(v$.newsletter.$dirty && v$.newsletter.$error)}]"><input type="radio" :value="false" v-model="v$.newsletter.$model" /> {{$t('account.signup.no')}}</label>
                    <div v-if="v$.newsletter.$dirty && v$.newsletter.$error" class="error">{{$t('account.signup.newsletter_error')}}</div>
                    <p>{{$t('account.signup.newsletter_explain2')}}</p>
                </fieldset>
                <fieldset>
                    <legend>{{$t('account.signup.tls')}}</legend>
                    <label :class="[
                        {error:(v$.cgv.$dirty && v$.cgv.$error)},
                        {valid:(v$.cgv.$dirty && !v$.cgv.$error)}]"><input type="checkbox" v-model="v$.cgv.$model" /> {{$t('account.signup.accept')}} <router-link :to="{'name': locale+'.tcs'}" target="_blank">{{$t('account.signup.tls')}}</router-link></label>
                    <div v-if="v$.cgv.$dirty && v$.cgv.$error" class="error">{{$t('account.signup.tls_error')}}</div>
                    <p>{{$t('account.signup.tls_explain')}}</p>
                </fieldset>
                <button type="submit">{{$t('account.signup.create')}}</button>
            </div>
        </form>
    </div>
</template>
<style lang="scss" scoped>
    .head-login {
        width: 100%;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        form{
            width: calc(100% - 40px);
            margin-left: 20px;
            margin-right: 20px;
            position: relative;
            padding: 20px;
            color: #FFF;
            
            &:before{
                content:'';
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                background: linear-gradient(257.47deg, #3C5A6B -8.78%, #0E2E5B 110.74%);
                z-index: -1;
                border-bottom-left-radius: 8px;
                border-bottom-right-radius: 8px;
            }
            h1{
                font-family: Montserrat;
                font-weight: 700;
                font-size: 30px;
                line-height: 36.57px;
                letter-spacing: 0%;
                text-align: center;
                text-transform: uppercase;
                margin-top: 20px;
                margin-bottom: 40px;
            }
            h2{
                font-family: Montserrat;
                font-weight: 700;
                font-size: 18px;
                line-height: 21.94px;
                letter-spacing: 0%;
                text-transform: uppercase;
                margin-bottom: 60px;
            }
            label{
                display: block;
                width: 100%;
                margin-bottom: 10px;
                padding-left: 10px;
                font-family: Montserrat;
                font-weight: 600;
                font-size: 14px;
                line-height: 17.07px;
                letter-spacing: 0%;
            }
            input{
                width: 100%;
                padding-left: 10px;
                padding-right: 10px;
                border-radius: 19px;
                border: none;
                margin-bottom: 20px;
                background-color: #FFF;
                font-size: 14px;
                line-height: 32px;
            }
            button[type="submit"]{
                width: 100%;
                padding: 10px;
                border-radius: 16px;
                border: none;
                background-color: #5DC5FF;
                

                color: #FFF;
                font-family: Montserrat;
                font-weight: 400;
                font-size: 14px;
                line-height: 17.07px;
                letter-spacing: 0%;
                text-align: center;
                margin-top: 20px;
                cursor: pointer;
                margin-bottom: 20px;
                &:hover{
                    background: #83cff8;
                }
            }
            a{
                color: #FFF;
                text-decoration: none;
                display: inline-block;
                margin: 0 auto;
                padding-left: 10px;
                font-family: Montserrat;
                font-weight: 400;
                font-size: 14px;
                line-height: 17.07px;
                letter-spacing: 0%;
                text-align: center;
                &:hover{
                    text-decoration: underline;
                }
            }
        }
    }
    .content{
        max-width: 1024px;
        margin: 0 auto;
        padding: 20px;
        h2{
            font-family: Montserrat;
            font-weight: 700;
            font-size: 18px;
            line-height: 21.94px;
            letter-spacing: 0%;
            text-transform: uppercase;
            margin-bottom: 20px;

        }
        
    }
    @media only screen and (min-width : $responsive-point) {
        .head-login {
            position: relative;
            width: $responsive-point;
            margin: 0 auto;
            flex-direction: row;
            form{
                width: 50%;
                margin: 0px;
                
                &:before{
                    left: calc(100% - (100vw / 2));
                    width: calc(100vw / 2);
                    border-radius: 0;
                }
                &:after{
                    top: 0;
                    left: 100%;
                    width: calc(100vw / 2);
                    content:'';
                    height: 100%;
                    background-image: url('/img/login/bkg_login.jpg');
                    background-size: cover;
                    background-position: center;
                    position: absolute;
                    z-index: -1;
                }
            }
        }
    }
</style>
<route lang="yaml">
name: account.login
meta:
    title: M'identifier - Kase
    layout: default
    middleware: guest
</route>